/**
 * Admin ThemeProvider — wraps `@sill/ui`'s tokens with the actual
 * provider implementation, localStorage persistence, and the
 * `document.documentElement.dataset.theme` reflection that activates
 * the CSS variable set published by `@sill/ui/css/theme`.
 *
 * Persistence key: `sill.admin.theme` (distinct from the dashboard's
 * `sill.dashboard.theme` so a staff member's admin theme preference
 * is not coupled to their merchant-dashboard preference — different
 * stations of use).
 *
 * Same shape as `apps/dashboard/src/lib/theme.tsx`; copy is
 * deliberate per ADR-0002 ("apps own the provider implementation").
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { darkC, lightC } from '@sill/ui';
import type { ThemeCtx, ThemeName, ThemeTokens } from '@sill/ui';

const STORAGE_KEY = 'sill.admin.theme';

const ThemeReactCtx = createContext<ThemeCtx>({
  C: darkC,
  theme: 'dark',
  setTheme: () => {
    /* no-op default */
  },
});

function readStoredTheme(): ThemeName | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw: unknown = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'dark' || raw === 'light') return raw;
  } catch {
    // Storage disabled — fall through to the default.
  }
  return null;
}

function writeStoredTheme(name: ThemeName): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, name);
  } catch {
    /* see readStoredTheme */
  }
}

function applyDocumentTheme(name: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset['theme'] = name;
}

export interface ThemeProviderProps {
  children: ReactNode;
  initial?: ThemeName;
}

export function ThemeProvider({ children, initial }: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    if (initial) return initial;
    return readStoredTheme() ?? 'dark';
  });

  useEffect(() => {
    applyDocumentTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);

  const value = useMemo<ThemeCtx>(() => {
    const C: ThemeTokens = theme === 'dark' ? darkC : lightC;
    return {
      C,
      theme,
      setTheme: (next) => setThemeState(next),
    };
  }, [theme]);

  return <ThemeReactCtx.Provider value={value}>{children}</ThemeReactCtx.Provider>;
}

export function useTheme(): ThemeCtx {
  return useContext(ThemeReactCtx);
}

export function useC(): ThemeTokens {
  return useContext(ThemeReactCtx).C;
}
